import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Dialog } from "@base-ui/react/dialog";
import { toast } from "sonner";
import { LayoutList } from "lucide-react";
import { useRepayments, usePayRepayment, useUpdateRepayment } from "@/hooks/useRepayments";
import { useCashAccounts } from "@/hooks/useCashAccounts";
import { useDrivers } from "@/hooks/useDrivers";
import { usePermissions } from "@/hooks/usePermissions";
import { useUrlState } from "@/hooks/useUrlState";
import { isAxiosError } from "axios";
import { DataTable } from "@/components/ui/data-table";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { Button } from "@/components/ui/button";
import { ListSummary } from "@/components/ui/list-summary";
import { RepaymentTypeBadge as TypeBadge, RepaymentStatusBadge } from "@/components/ui/repayment-badges";
import { LoadingButton } from "@/components/ui/loading-button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ComboboxSelect } from "@/components/form/combobox-select";
import { cn, formatRepaymentCode, formatFreightCode } from "@/lib/utils";
import {
  getRepaymentDisplayStatus,
  isBalanceBlocked,
  BALANCE_BLOCKED_REASON,
} from "@/lib/repayment-status";
import type { Repayment } from "@/types/repayment";
import type { Column } from "@/components/ui/data-table";

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatMoney(value: string | number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value));
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-BR");
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  PIX: "PIX",
  BANK_TRANSFER: "Transferência",
  BANK_SLIP: "Boleto",
  CASH: "Dinheiro",
  OTHER: "Outro",
};

const PAYMENT_METHODS = [
  { value: "PIX", label: "PIX" },
  { value: "BANK_TRANSFER", label: "Transferência bancária" },
  { value: "BANK_SLIP", label: "Boleto" },
  { value: "CASH", label: "Dinheiro" },
  { value: "OTHER", label: "Outro" },
] as const;

// ─── Pay Modal (T-802) ───────────────────────────────────────────────────────

const paySchema = z.object({
  paymentDate: z.string().min(1, "Data obrigatória"),
  method: z.enum(["PIX", "BANK_TRANSFER", "BANK_SLIP", "CASH", "OTHER"]),
  notes: z.string().optional(),
  accountId: z.string().min(1, "Conta obrigatória"),
});

type PayFormData = z.infer<typeof paySchema>;

const selectClass = cn(
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
);

function PayRepaymentModal({
  repayment,
  open,
  onClose,
  onSuccess,
}: {
  repayment: Repayment;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { mutate: pay, isPending } = usePayRepayment();
  const { data: accountsData } = useCashAccounts({ active: true, perPage: 100 });
  const accounts = accountsData?.data ?? [];

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PayFormData>({
    resolver: zodResolver(paySchema),
    defaultValues: { paymentDate: todayISO(), method: "PIX", notes: "", accountId: "" },
  });

  useEffect(() => {
    if (!open) reset({ paymentDate: todayISO(), method: "PIX", notes: "", accountId: "" });
  }, [open, reset]);

  useEffect(() => {
    if (open && accounts.length > 0) {
      setValue("accountId", accounts.find((a) => a.isDefault)?.id ?? accounts[0]!.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountsData]);

  function onSubmit(data: PayFormData) {
    pay(
      {
        id: repayment.id,
        data: {
          paymentDate: new Date(data.paymentDate).toISOString(),
          method: data.method,
          notes: data.notes || undefined,
          accountId: data.accountId,
        },
      },
      {
        onSuccess: () => {
          toast.success("Pagamento registrado com sucesso");
          onSuccess();
        },
        onError: (err: Error) => {
          toast.error(err.message || "Erro ao registrar pagamento");
        },
      },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Registrar Pagamento
          </Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">
            Valor integral:{" "}
            <span className="font-semibold text-foreground">
              {formatMoney(repayment.amount)}
            </span>
            <span className="mx-2">·</span>
            <TypeBadge type={repayment.type} />
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="paymentDate">Data *</Label>
                <Input
                  id="paymentDate"
                  type="date"
                  aria-invalid={!!errors.paymentDate}
                  {...register("paymentDate")}
                />
                {errors.paymentDate && (
                  <p className="text-xs text-destructive">
                    {errors.paymentDate.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="method">Forma *</Label>
                <Controller
                  name="method"
                  control={control}
                  render={({ field }) => (
                    <select
                      id="method"
                      value={field.value}
                      onChange={field.onChange}
                      className={selectClass}
                    >
                      {PAYMENT_METHODS.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="accountId">Conta de pagamento *</Label>
              <Controller
                name="accountId"
                control={control}
                render={({ field }) => (
                  <select
                    id="accountId"
                    value={field.value}
                    onChange={field.onChange}
                    className={selectClass}
                  >
                    <option value="" disabled>
                      Selecione…
                    </option>
                    {accounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.accountId && (
                <p className="text-xs text-destructive">{errors.accountId.message}</p>
              )}
              {accounts.length === 0 && (
                <p className="text-xs text-destructive">
                  Cadastre uma conta de caixa antes de registrar pagamentos.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Observações</Label>
              <Input id="notes" {...register("notes")} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isPending}
              >
                Cancelar
              </Button>
              <LoadingButton
                type="submit"
                loading={isPending}
                loadingText="Registrando…"
              >
                Registrar
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Edit Modal ──────────────────────────────────────────────────────────────

const editSchema = z.object({
  amount: z.coerce.number().positive("Valor deve ser positivo"),
});

type EditFormData = z.infer<typeof editSchema>;

function EditRepaymentModal({
  repayment,
  open,
  onClose,
  onSuccess,
}: {
  repayment: Repayment;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { mutate: update, isPending } = useUpdateRepayment();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.input<typeof editSchema>, unknown, EditFormData>({
    resolver: zodResolver(editSchema),
    defaultValues: { amount: Number(repayment.amount) },
  });

  useEffect(() => {
    if (open) reset({ amount: Number(repayment.amount) });
  }, [open, repayment.amount, reset]);

  function onSubmit(data: EditFormData) {
    update(
      { id: repayment.id, data: { amount: data.amount } },
      {
        onSuccess: () => {
          toast.success("Repasse atualizado");
          onSuccess();
        },
        onError: (err: Error) => {
          const msg = isAxiosError(err)
            ? (err.response?.data as { error?: { message?: string } } | undefined)?.error?.message
            : undefined;
          toast.error(msg ?? "Erro ao atualizar repasse");
        },
      },
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-background p-6 shadow-md">
          <Dialog.Title className="text-lg font-semibold text-foreground">
            Editar Repasse
          </Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">
            <TypeBadge type={repayment.type} />
            <span className="mx-2">·</span>
            Valor atual: <span className="font-semibold text-foreground">{formatMoney(repayment.amount)}</span>
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="amount">Novo valor *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                aria-invalid={!!errors.amount}
                {...register("amount")}
              />
              {errors.amount && (
                <p className="text-xs text-destructive">{errors.amount.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancelar
              </Button>
              <LoadingButton type="submit" loading={isPending} loadingText="Salvando…">
                Salvar
              </LoadingButton>
            </div>
          </form>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Repayment Drawer (T-801) ────────────────────────────────────────────────

function RepaymentDrawer({
  repayment,
  open,
  onClose,
}: {
  repayment: Repayment | null;
  open: boolean;
  onClose: () => void;
}) {
  const [payOpen, setPayOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { has } = usePermissions();
  const canEdit = has("repayment.update");

  function handlePaySuccess() {
    setPayOpen(false);
    onClose();
  }

  function handleEditSuccess() {
    setEditOpen(false);
    onClose();
  }

  if (!repayment) return null;

  const balanceBlocked = isBalanceBlocked(repayment);

  return (
    <>
      <Dialog.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/50" />
          <Dialog.Popup className="fixed inset-y-0 right-0 z-40 flex h-full w-full max-w-md flex-col border-l border-border bg-background shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <Dialog.Title className="flex items-center gap-2 text-base font-semibold text-foreground">
                  <span className="rounded-md bg-muted px-2 py-0.5 font-mono text-xs">
                    {formatRepaymentCode(repayment.code)}
                  </span>
                  Detalhe do Repasse
                </Dialog.Title>
                <p className="text-sm text-muted-foreground">
                  {repayment.driver.name}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0 text-lg"
              >
                ×
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              <div className="flex items-center gap-3">
                <TypeBadge type={repayment.type} />
                <RepaymentStatusBadge
                  status={getRepaymentDisplayStatus(repayment)}
                />
              </div>

              <dl className="grid grid-cols-2 gap-x-4 gap-y-5">
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">
                    Valor
                  </dt>
                  <dd className="mt-0.5 text-xl font-bold text-foreground">
                    {formatMoney(repayment.amount)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">
                    Criado em
                  </dt>
                  <dd className="mt-0.5 text-sm text-foreground">
                    {formatDate(repayment.createdAt)}
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs font-medium text-muted-foreground">
                    Frete
                  </dt>
                  <dd className="mt-0.5 flex items-center gap-2 text-sm text-foreground">
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                      {formatFreightCode(repayment.freight.code)}
                    </span>
                    {repayment.freight.originCity}/{repayment.freight.originState}{" "}
                    →{" "}
                    {repayment.freight.destinationCity}/
                    {repayment.freight.destinationState}
                  </dd>
                </div>
              </dl>

              {repayment.payment && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <p className="text-xs font-semibold text-green-700 mb-3">
                    Pagamento registrado
                  </p>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div>
                      <dt className="text-xs text-muted-foreground">Valor</dt>
                      <dd className="font-medium">
                        {formatMoney(repayment.payment.amount)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Data</dt>
                      <dd>{formatDate(repayment.payment.paymentDate)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Forma</dt>
                      <dd>
                        {PAYMENT_METHOD_LABEL[repayment.payment.method] ??
                          repayment.payment.method}
                      </dd>
                    </div>
                    {repayment.payment.notes && (
                      <div className="col-span-2">
                        <dt className="text-xs text-muted-foreground">Obs.</dt>
                        <dd>{repayment.payment.notes}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>

            {/* Footer */}
            {repayment.status === "PENDING" && (
              <div className="flex flex-col gap-2 border-t border-border px-6 py-4">
                {balanceBlocked && (
                  <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {BALANCE_BLOCKED_REASON}
                  </p>
                )}
                <Button
                  className="w-full"
                  onClick={() => setPayOpen(true)}
                  disabled={balanceBlocked}
                >
                  Registrar Pagamento
                </Button>
                {canEdit && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setEditOpen(true)}
                  >
                    Editar valor
                  </Button>
                )}
              </div>
            )}
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>

      {repayment && payOpen && (
        <PayRepaymentModal
          repayment={repayment}
          open={payOpen}
          onClose={() => setPayOpen(false)}
          onSuccess={handlePaySuccess}
        />
      )}

      {repayment && editOpen && (
        <EditRepaymentModal
          repayment={repayment}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </>
  );
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

const TYPE_OPTIONS = [
  { value: "", label: "Todos os tipos" },
  { value: "ADVANCE", label: "Adiantamento" },
  { value: "BALANCE", label: "Saldo" },
];

const STATUS_OPTIONS = [
  { value: "", label: "Todos os status" },
  { value: "PENDING", label: "Pendente" },
  { value: "PAID", label: "Pago" },
];

// ─── Page (T-800) ────────────────────────────────────────────────────────────

const COLUMNS: Column<Repayment>[] = [
  {
    key: "code",
    header: "Código",
    className: "w-20",
    render: (row) => (
      <span className="font-mono text-xs font-medium text-foreground">
        {formatRepaymentCode(row.code)}
      </span>
    ),
  },
  {
    key: "driver",
    header: "Motorista",
    render: (row) => (
      <span className="font-medium text-foreground">{row.driver.name}</span>
    ),
  },
  {
    key: "type",
    header: "Tipo",
    render: (row) => <TypeBadge type={row.type} />,
  },
  {
    key: "freight",
    header: "Frete",
    render: (row) => (
      <span>
        <span className="mr-1.5 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
          {formatFreightCode(row.freight.code)}
        </span>
        {row.freight.originCity}/{row.freight.originState} → {row.freight.destinationCity}/{row.freight.destinationState}
      </span>
    ),
  },
  {
    key: "amount",
    header: "Valor",
    render: (row) => (
      <span className="font-medium">{formatMoney(row.amount)}</span>
    ),
  },
  {
    key: "status",
    header: "Status",
    render: (row) => (
      <RepaymentStatusBadge status={getRepaymentDisplayStatus(row)} />
    ),
  },
  {
    key: "createdAt",
    header: "Criado em",
    render: (row) => formatDate(row.createdAt),
  },
];

export function RepaymentListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useUrlState<string>("search", "");
  const [driverFilter, setDriverFilter] = useUrlState<string>("driverId", "");
  const [typeFilter, setTypeFilter] = useUrlState<string>("type", "");
  const [statusFilter, setStatusFilter] = useUrlState<string>("status", "");
  const [page, setPage] = useUrlState<number>("page", 1);
  const [selected, setSelected] = useState<Repayment | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: driversData } = useDrivers({ active: true, perPage: 200 });
  const driverOptions = [
    { value: "", label: "Todos os motoristas" },
    ...(driversData?.data ?? []).map((d) => ({ value: d.id, label: d.name })),
  ];

  const { data, isLoading } = useRepayments({
    search: search || undefined,
    driverId: driverFilter || undefined,
    type: typeFilter || undefined,
    status: statusFilter || undefined,
    page,
  });

  function handleSearch(v: string) {
    setSearch(v);
    setPage(1);
  }

  function handleFilter(setter: (v: string) => void) {
    return (v: string) => {
      setter(v);
      setPage(1);
    };
  }

  function handleRowClick(row: Repayment) {
    setSelected(row);
    setDrawerOpen(true);
  }

  function handleDrawerClose() {
    setDrawerOpen(false);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Repasses</h1>
          <p className="text-sm text-muted-foreground">
            Adiantamentos e saldos dos motoristas
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate("/repayments/consolidated")}
        >
          <LayoutList size={16} />
          Visão por motorista
        </Button>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <SearchInput
          value={search}
          onChange={handleSearch}
          placeholder="Buscar por código (ex: R-12 ou F-6)…"
          className="w-full sm:w-72"
        />
        <ComboboxSelect
          options={driverOptions}
          value={driverFilter}
          onChange={handleFilter(setDriverFilter)}
          placeholder="Todos os motoristas"
          searchPlaceholder="Buscar motorista..."
          className="w-full sm:w-56"
        />
        <ComboboxSelect
          options={TYPE_OPTIONS}
          value={typeFilter}
          onChange={handleFilter(setTypeFilter)}
          placeholder="Todos os tipos"
          className="w-full sm:w-44"
        />
        <ComboboxSelect
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={handleFilter(setStatusFilter)}
          placeholder="Todos os status"
          className="w-full sm:w-40"
        />
      </div>

      {data && (
        <ListSummary
          metrics={[
            {
              label: "A pagar",
              value: formatMoney(data.totals.pendingAmount),
              tone:
                Number.parseFloat(data.totals.pendingAmount) > 0 ? "warning" : "neutral",
              hint: `${data.total} ${data.total === 1 ? "repasse" : "repasses"}`,
            },
            {
              label: "Pago",
              value: formatMoney(data.totals.paidAmount),
              tone: "positive",
            },
          ]}
        />
      )}

      <DataTable
        columns={COLUMNS}
        data={data?.data ?? []}
        keyExtractor={(row) => row.id}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        emptyTitle="Nenhum repasse encontrado"
        emptyDescription="Repasses são criados ao atribuir um motorista a um frete."
      />

      {data && data.totalPages > 1 && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={data.perPage}
          onChange={setPage}
        />
      )}

      <RepaymentDrawer
        repayment={selected}
        open={drawerOpen}
        onClose={handleDrawerClose}
      />
    </div>
  );
}
